if (customElements.get('klaviyo-controller') === undefined) {
  // @ts-check
  /**
   * KlaviyoController handles back-in-stock notifications and drawer UI logic.
   * @class
   * @extends HTMLElement
   * @property {string|null} productHandle
   * @property {AbortController|undefined} abortController
   * @property {Function|null} variantChangeUnsubscribe
   * @property {string|number|null} variantId
   * @property {HTMLElement|null} sizeElement
   * @property {HTMLInputElement|null} variantInput
   * @property {HTMLFormElement|null} backInStockForm
   * @property {string|null} publicKey
   * @property {HTMLElement|null} notifElement
   * @property {number|null} resetNotifTimeout
   * @property {Function} _infoLoadedHandler
   * @property {string|null} termsAndRequiredLabel
   * @property {string|null} successLabel
   * @property {string|null} validEmailLabel
   * @property {HTMLInputElement|null} emailInput
   * @property {HTMLElement|undefined} colorElement
   * @property {Function} _drawerOpen
   * @property {Function} _drawerClose
   */

  class KlaviyoController extends HTMLElement {
    constructor() {
      super();
      this.productHandle = null;
      this.abortController = undefined;
      this.variantChangeUnsubscribe = null;
      this.variantId = null;
      this.sizeElement = null;
      this.variantInput = null;
      this.backInStockForm = null;
      this.publicKey = null;
      this.notifElement = null;
      // @ts-ignore
      this.resetNotifTimeout = null;
      this._infoLoadedHandler = this.handleInfoLoaded.bind(this); //
      this.termsAndRequiredLabel = null;
      this.successLabel = null;
      this.validEmailLabel = null;
      this.emailInput = null;
      this.colorElement = null;
      this._drawerOpen = () => {
        /** @type {HTMLElement|null} */
        const drawer = /** @type {HTMLElement|null} */ document.querySelector(
          '#backinstock-drawer'
        );

        if (drawer && typeof (/** @type {any} */ (drawer).open) === 'function')
          /** @type {{ open?: () => void }} */ (drawer).open();
      };

      this._drawerClose = () => {
        /** @type {HTMLElement|null} */
        const drawer = /** @type {HTMLElement|null} */ document.querySelector(
          '#backinstock-drawer'
        );
        if (drawer && typeof (/** @type {any} */ (drawer).close) === 'function')
          /** @type {{ close?: () => void }} */ (drawer).close();
      };
    }

    connectedCallback() {
      this.backInStockForm = /** @type {HTMLFormElement|null} */ (
        this.querySelector('#back-in-stock-form')
      ); //get the back in stock form
      this.variantInput = /** @type {HTMLInputElement|null} */ (
        this.querySelector('#backInStockVariantId')
      );
      this.sizeElement =
        /** @type {HTMLElement|null} */ this.querySelector('#klaviyo-size'); // store the element once connected
      this.emailInput =
        /** @type {HTMLInputElement|null} */ this.querySelector('#email-input');
      this.colorElement =
        /** @type {HTMLElement|null} */ this.querySelector('#klaviyo-color');
      this.notifElement = /** @type {HTMLElement|null} */ this.querySelector(
        '#notification-message'
      );
      /** @type {string} */
      this.customAriaAvailable =
        /** @type {string} */ this.getAttribute('aria-available') || 'false';
      this.variantId = this.getAttribute('data-firstAvailableVariantId'); // get initial variant id
      this.publicKey = this.getAttribute('data-company'); //get company id for api requirements
      this.termsAndRequiredLabel = this.getAttribute('data-termsRequiredLabel');
      this.validEmailLabel = this.getAttribute('data-validEmailLabel');

      //subscribe to variant change
      // @ts-ignore
      this.variantChangeUnsubscribe = subscribe(
        // @ts-ignore
        PUB_SUB_EVENTS.variantChange,
        this.handleVariantChange.bind(this)
      );

      // Get the success label from the <template> inside this element
      /** @type {HTMLTemplateElement|null} */
      const template = /** @type {HTMLTemplateElement|null} */ (
        this.querySelector('#klaviyo-success-label')
      );
      if (template && template.content) {
        this.successLabel = template.content.textContent.trim();
      } else {
        this.successLabel = null;
      }

      this.handleChangeVariantInputValue(`${this.variantId}`); //change input value to current variant id

      this.handleSubmit({
        publicKey: this.publicKey,
        backInStockForm: this.backInStockForm,
        notifElement: /** @type {HTMLElement|null} */ (this.notifElement),
        termsAndRequiredLabel: this.termsAndRequiredLabel,
        successLabel: this.successLabel,
        validEmailLabel: this.validEmailLabel,
      });

      this.handleDrawerEvents();

      //subscribe to product info event
      window.addEventListener('product-info:loaded', this._infoLoadedHandler);
      window.addEventListener('product-info:loaded', (e) => {
        this.setAttribute(
          'aria-available',
          this.customAriaAvailable || 'false'
        );
      });
    }

    //cleanups!
    disconnectedCallback() {
      if (this.abortController) {
        this.abortController.abort();
      }

      if (this.resetNotifTimeout) {
        window.clearTimeout(this.resetNotifTimeout);
      }

      if (typeof this.variantChangeUnsubscribe === 'function') {
        this.variantChangeUnsubscribe();
      }

      window.removeEventListener(
        'product-info:loaded',
        this._infoLoadedHandler
      );
      this.handleResetNotif();
      this.handleDrawerEvents(false);
    }

    //this will be called only when the event product-info:loaded
    handleInfoLoaded() {
      const colorElementSelected = document.querySelector(
        '[data-selected-value]'
      );
      const sizeElementSelected = document.querySelector('[data-size="true"]');

      // @ts-ignore - lets add proper type as this is a experimental if jsdoc will help
      if (
        sizeElementSelected &&
        this.sizeElement &&
        sizeElementSelected.value
      ) {
        // @ts-ignore - lets add proper type as this is a experimental if jsdoc will help
        this.sizeElement.textContent = sizeElementSelected.value;
      }

      // @ts-ignore - lets add proper type as this is a experimental if jsdoc will help
      if (
        colorElementSelected &&
        this.colorElement &&
        colorElementSelected.innerHTML
      ) {
        // @ts-ignore - lets add proper type as this is a experimental if jsdoc will help
        this.colorElement.textContent = colorElementSelected.textContent;
      }

      this.handleDrawerEvents();
    }

    //this will be called when the variant change event was called

    /**
     * Handles updates when the product variant changes (e.g., size or color selection).
     * @param {Object} e - The event object from the variant change pub/sub.
     * @param {Object} e.data - The event data.
     * @param {Object} e.data.variant - The variant object.
     * @param {string} [e.data.variant.option2] - The size label (if available).
     * @param {string} [e.data.variant.option1] - The color label (if available).
     * @param {string|number} [e.data.variant.id] - The variant ID.
     * @param {boolean} [e.data.variant.available] - The variant availability.
     * @returns {void}
     */
    handleVariantChange(e) {
      console.log('Klaviyo Variant Change Event:', e);
      const sizeLabel = e?.data?.variant?.option2; // get data from subscribe variant change
      const colorLabel = e?.data?.variant?.option1; // get data from subscribe variant change
      this.variantId = e?.data?.variant?.id; // get data from subscribe variant change

      if (sizeLabel && this.sizeElement) {
        this.sizeElement.innerHTML = sizeLabel;
      }

      if (colorLabel && this.colorElement) {
        this.colorElement.innerHTML = colorLabel;
      }

      this.setAttribute('aria-available', `${e?.data.variant?.available}`);

      this.handleChangeVariantInputValue(`${this.variantId}`); //change input value via variant change
      this.handleResetNotif();
    }

    /**
     * Update the variant input value in the form.
     * @param {string} variantId - The variant ID to set as the input value.
     * @returns {void}
     */
    handleChangeVariantInputValue(variantId) {
      if (this.variantInput) {
        this.variantInput.value = variantId.toString();
      }
    }

    /**
     * Handles the back-in-stock form submission and notification logic.
     * @param {Object} params - The parameters object.
     * @param {string|null} params.publicKey - The Klaviyo public API key.
     * @param {HTMLFormElement|null} params.backInStockForm - The back-in-stock form element.
     * @param {HTMLElement|null} params.notifElement - The notification element.
     * @param {string|null} params.termsAndRequiredLabel - The label for required terms.
     * @param {string|null} params.successLabel - The label for success notification.
     * @param {string|null} params.validEmailLabel - The label for valid email notification.
     * @returns {void}
     */
    handleSubmit({
      publicKey,
      backInStockForm,
      notifElement,
      termsAndRequiredLabel,
      successLabel,
      validEmailLabel,
    }) {
      this.abortController = new AbortController();
      const signal = this.abortController.signal;
      const handleResetNotif = this.handleResetNotif;
      const handleShowNotif = this.handleShowNotif;
      // const handleLoadingState = this.handleLoadingState;
      const test = false;

      if (backInStockForm) {
        if (this.emailInput) {
          this.emailInput.addEventListener('focus', () => {
            if (notifElement) handleResetNotif(notifElement);
          });
        }

        backInStockForm.addEventListener('submit', async (event) => {
          event.preventDefault();
          // Set loading state on submit

          // Create a FormData object from the form
          const formData = new FormData(backInStockForm); // use the new FormData API
          const data = Object.fromEntries(formData); // Convert to object
          const { email, backInStockVariantId, bisTermsAndCondition } = data; // object destructure
          // @ts-ignore
          const emailFilteredValue = email.trim(); // trim email of whitespace

          if (!emailFilteredValue) {
            handleShowNotif({
              notifElement: notifElement,
              message: validEmailLabel || 'Please enter a valid email address.',
              isError: true,
            });

            console.error('Please enter a valid email address.');

            return null;
          }

          if (!backInStockVariantId) {
            handleShowNotif({
              notifElement: notifElement,
              message: 'Something went wrong while processing your request.',
              isError: true,
            });

            console.error('PDP Variant ID is missing');

            return null;
          }

          if (!publicKey) {
            handleShowNotif({
              notifElement: notifElement,
              message: 'Something went wrong',
              isError: true,
            });

            console.error('MISSING API KEY');

            return null;
          }

          if (!bisTermsAndCondition) {
            handleShowNotif({
              notifElement: notifElement,
              message:
                termsAndRequiredLabel ||
                'Please accept the terms and conditions.',
              isError: true,
            });

            console.error('Need terms and required label');

            return null;
          }

          if (notifElement) {
            handleResetNotif(notifElement);
          }

          // Construct the payload for Klaviyo's client-side API
          const payload = {
            data: {
              type: 'back-in-stock-subscription',
              attributes: {
                profile: {
                  data: {
                    type: 'profile',
                    attributes: {
                      email: emailFilteredValue,
                    },
                  },
                },
                channels: ['EMAIL'], // Use ['SMS'] for phone number if needed
              },
              relationships: {
                variant: {
                  data: {
                    type: 'catalog-variant',
                    id: `$shopify:::$default:::${backInStockVariantId}`,
                  },
                },
              },
            },
          };

          // Configure the request options
          const requestOptions = {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              revision: '2024-06-15', // Latest API revision as of May 2025
            },
            body: JSON.stringify(payload), //stringify the payload
            signal: signal,
          };

          this.handleLoadingState(true);

          if (!test) {
            await fetch(
              `https://a.klaviyo.com/client/back-in-stock-subscriptions/?company_id=${publicKey}`,
              requestOptions
            )
              .then((result) => {
                if (result.status === 202) {
                  handleShowNotif({
                    notifElement: notifElement,
                    message: successLabel || 'Thank you for subscribing!',
                    isError: false,
                  });
                  backInStockForm.reset();
                } else {
                  handleShowNotif({
                    notifElement: notifElement,
                    message:
                      // @ts-ignore
                      result?.errors?.[0]?.detail ||
                      'Something went wrong. Please try again.',
                    isError: true,
                  });
                }
              })
              .catch((error) => {
                handleShowNotif({
                  notifElement: notifElement,
                  message: 'Error: Unable to process your request.',
                  isError: true,
                });

                console.error('Fetch error:', error);
              })
              .finally(() => {
                this.resetNotifTimeout = setTimeout(() => {
                  if (notifElement) {
                    handleResetNotif(notifElement);
                  }
                }, 2000);
                this.handleLoadingState(false); // Reset loading state after request
              });
          }
        });
      }
    }

    /**
     * Show a notification message in the notification element.
     * @param {Object} params - The parameters object.
     * @param {HTMLElement|null|undefined} params.notifElement - The notification element to update.
     * @param {string} params.message - The message to display.
     * @param {boolean} params.isError - Whether the message is an error.
     * @returns {void}
     */
    handleShowNotif({ notifElement, message, isError }) {
      const notifEle = notifElement || this.notifElement;

      if (!notifEle) {
        console.info('Notification Element missing');

        return;
      }

      notifEle.textContent = message;
      notifEle.setAttribute('aria-error', `${isError}`);
    }

    /**
     * Reset (clear) the notification message in the notification element.
     * @param {HTMLElement|undefined} [notifElement] - The notification element to clear.
     * @returns {void}
     */
    handleResetNotif(notifElement) {
      const notifEle = notifElement || this.notifElement;

      if (!notifEle) {
        console.info('Unable to remove text on Notification Element');

        return;
      }

      notifEle.innerHTML = '';
    }

    /**
     * Add or remove event listeners for opening and closing the back-in-stock drawer.
     * @param {boolean} [add=true] - Whether to add (true) or remove (false) the event listeners.
     * @returns {void}
     */
    handleDrawerEvents(add = true) {
      const openButtons = document.querySelectorAll(
        '.js-backinstock-open-drawer'
      );
      const closeButton = document.querySelector('#backinstock-close-drawer');

      openButtons.forEach((btn) =>
        btn[add ? 'addEventListener' : 'removeEventListener'](
          'click',
          this._drawerOpen
        )
      );
      if (closeButton) {
        closeButton[add ? 'addEventListener' : 'removeEventListener'](
          'click',
          this._drawerClose
        );
      }
    }

    /**
     * Handles the loading state by setting the appropriate ARIA attribute.
     * @param {boolean} isLoading - Whether the loading state is active.
     * @returns {void}
     */
    handleLoadingState(isLoading) {
      this.setAttribute('aria-busy', `${isLoading}`);
    }
  }

  customElements.define('klaviyo-controller', KlaviyoController);
}
